import { useState } from 'react';

import useIsOpen from 'hooks/useIsOpen';
import { useGetAdminSettings } from 'queries';
import { useTranslation } from 'react-i18next';
import { v4 as uuidv4 } from 'uuid';

import { ErrorDetail, YearClosureProgressData } from './types';

import { Dialog, Drawer } from 'components/shared';
import TableCRUDLayout from 'components/shared/CRUD/TableCRUDLayout';

import { CloseYearFormComponent } from './components/CloseYearFormComponent';
import { ErrorDataGrid } from './components/ErrorDataGrid/ErrorDataGrid';

import EventBus from '../../../../lib/EventBus';
import { useCloseYear } from './hooks/useCloseYear';
import { useGetColumnDefs } from './hooks/useGetColumnDefs';
import { useGetTableData } from './hooks/useGetTableData';
import { useOpenYear } from './hooks/useOpenYear';

const eventBusUrlCurrent =
  '/calculation/hours-management/year-closure/current-year';
const eventBusUrlNext = '/calculation/hours-management/year-closure/next-year';

const extendWithId = (data) =>
  data.errorDetails.map((item) => ({
    ...item,
    id: uuidv4()
  }));

export const YearClosure = () => {
  const { t } = useTranslation('common');
  const { isOpen, open, close } = useIsOpen();
  const {
    isOpen: isDialogOpen,
    open: openDialog,
    close: closeDialog
  } = useIsOpen();
  const { data: settingsRes } = useGetAdminSettings('year-end-procedure');
  const settings = settingsRes ? settingsRes[0] : undefined;
  const { mutate: closeCurrentYear } = useCloseYear();
  const [currentYearProgress, setCurrentYearProgress] =
    useState<YearClosureProgressData | null>(null);
  const [nextYearProgress, setNextYearProgress] =
    useState<YearClosureProgressData | null>(null);
  const [currentYearErrors, setCurrentYearErrors] = useState<ErrorDetail[]>([]);
  const [nextYearErrors, setNextYearErrors] = useState<ErrorDetail[]>([]);
  const [showedErrors, setShowedErrors] = useState<ErrorDetail[]>([]);
  const { mutate: openCurrentYear } = useOpenYear();

  const onCurrentYearClosureCb = (data: YearClosureProgressData) => {
    setCurrentYearProgress(data);

    if (data.errorDetails.length > 0) {
      const errorWithId = extendWithId(data);
      setCurrentYearErrors((prev) => prev.concat(errorWithId));
    }

    if (data.hasFinished) {
      EventBus.unregister(eventBusUrlCurrent, onCurrentYearClosureCb);
    }
  };

  const onNextYearClosureCb = (data: YearClosureProgressData) => {
    setNextYearProgress(data);

    if (data.errorDetails.length > 0) {
      const errorWithId = extendWithId(data);
      setNextYearErrors((prev) => prev.concat(errorWithId));
    }

    if (data.hasFinished) {
      EventBus.unregister(eventBusUrlNext, onNextYearClosureCb);
    }
  };

  const unregisterWS = () => {
    EventBus.unregister(eventBusUrlCurrent, onCurrentYearClosureCb);
    EventBus.unregister(eventBusUrlNext, onNextYearClosureCb);
  };

  const registerWS = () => {
    unregisterWS();
    EventBus.register(eventBusUrlCurrent, onCurrentYearClosureCb);
    EventBus.register(eventBusUrlNext, onNextYearClosureCb);
  };

  const onCloseDialog = () => {
    closeDialog();
  };

  const onExited = () => {
    setShowedErrors([]);
  };

  const showCurrentYearErrors = () => {
    setShowedErrors(currentYearErrors);
    openDialog();
  };

  const showNextYearErrors = () => {
    setShowedErrors(nextYearErrors);
    openDialog();
  };

  const closeYear = (id, row) => {
    setRowData(row);
    open();
  };

  const openYear = (id, row) => {
    openCurrentYear(row?.year);
  };

  const onCloseDrawer = () => {
    close();
    setRowData(null);
  };

  const columns = useGetColumnDefs({
    openYear: openYear,
    closeYear: closeYear,
    onCurrentYearErrorClick: showCurrentYearErrors,
    onNextYearErrorClick: showNextYearErrors,
    currentYearProgress: currentYearProgress,
    nextYearProgress: nextYearProgress,
    currentYearErrors: currentYearErrors,
    nextYearErrors: nextYearErrors
  });
  const { data, isFetching } = useGetTableData();
  const [rowData, setRowData] = useState(null);

  const startYearClosure = async () => {
    await closeCurrentYear();

    setNextYearErrors([]);
    setCurrentYearErrors([]);

    registerWS();
  };

  return (
    <>
      <Dialog
        slotProps={{
          paper: {
            sx: {
              minWidth: 700
            }
          }
        }}
        open={isDialogOpen}
        TransitionProps={{ onExited: onExited }}
        handleClose={onCloseDialog}
        title={t('yearClosure.progress_bar.error_details_grid.title')}
      >
        <ErrorDataGrid errors={showedErrors} />
      </Dialog>
      <Drawer
        title={t('yearClosure.close_year')}
        onClose={onCloseDrawer}
        open={isOpen}
      >
        <CloseYearFormComponent
          rowData={rowData}
          settings={settings}
          handleClose={onCloseDrawer}
          startYearClosure={startYearClosure}
        />
      </Drawer>
      <TableCRUDLayout
        columns={columns}
        FormComponent={CloseYearFormComponent}
        rows={data}
        getRowId={(data) => data.year}
        getRowHeight={() => 'auto'}
        editable={false}
        hideCreateAction={true}
        loading={isFetching}
      />
    </>
  );
};
